/**
 * Карточка видео-референса `/video-refs/:refId` (спека §12). Слева (липко) — плеер, лента ритма и источник;
 * справа — статус, действия (переразбор, ПЛАТНОЕ описание, ПЛАТНОЕ «Создать проект»), общий комментарий,
 * сводка описания и сцены; внизу — проекты по этому референсу.
 *
 * Пока референс скачивается или разбирается, `useVideoRef` опрашивает раз в 3 с. Правки сцен отдают свежий
 * `detail` прямо в кэш. Комментарии — автосохранение через 1 с (`useNoteAutosave`); перед платным вызовом
 * очередь дожидается сохранения: комментарии уходят композеру.
 */
import { Film } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router";

import { Crumbs, Empty, ErrorLine, Hint, PageState, Panel } from "@/components/project/ui";
import { AddSceneForm } from "@/components/videoref/AddSceneForm";
import { RefActions } from "@/components/videoref/RefActions";
import { RefSource } from "@/components/videoref/RefSource";
import { SceneCard } from "@/components/videoref/SceneCard";
import { SceneRibbon } from "@/components/videoref/SceneRibbon";
import { mediaUrl, thumbUrl, when } from "@/lib/media";
import { errorText, usd } from "@/lib/project";
import { markedLabel, REF_NOTE, sceneNoteKey, type VideoRefDetail } from "@/lib/videoRef";
import { refErrorText, useNoteAutosave, useSceneOps, useVideoRef } from "@/lib/videoRefApi";

const SUMMARY: { key: "hook" | "tempo" | "works" | "structure"; label: string }[] = [
  { key: "hook", label: "Хук" },
  { key: "tempo", label: "Темп" },
  { key: "works", label: "Что работает" },
  { key: "structure", label: "Структура" },
];

export function VideoRefPage() {
  const { refId } = useParams();
  const id = Number(refId);
  const existing = Boolean((useLocation().state as { existing?: boolean } | null)?.existing);
  if (!Number.isInteger(id) || id <= 0) return <PageState loading={false} error="нет такого референса" />;
  // хуки комментариев и платных ключей — на свой референс: другой id монтирует карточку заново
  return <RefCard key={id} id={id} existing={existing} />;
}

function RefCard({ id, existing }: { id: number; existing: boolean }) {
  const q = useVideoRef(id);
  const notes = useNoteAutosave(id);
  const video = useRef<HTMLVideoElement>(null);
  const [playerMs, setPlayerMs] = useState(0);

  if (!q.data) return <PageState loading={q.isPending} error={q.isError ? errorText(q.error) : null} />;
  const detail = q.data;
  const info = detail.ref;

  const seek = (ms: number) => {
    const el = video.current;
    if (el) el.currentTime = ms / 1000;
    setPlayerMs(ms);
  };

  return (
    <div className="grid content-start gap-4 p-5">
      <div>
        <Crumbs items={[{ label: "Видео-референсы", to: "/video-refs" }, { label: `Референс ${info.id}` }]} />
        <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg">
          {info.title || info.uploader || `Референс ${info.id}`}
        </h1>
      </div>
      {existing ? (
        <div className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
          Такой ролик уже есть
        </div>
      ) : null}

      <div className="grid grid-cols-[minmax(260px,360px)_minmax(0,1fr)] items-start gap-5">
        <div className="sticky top-4 grid gap-3" data-annot="видео-референс: плеер">
          {info.asset_path ? (
            <video
              ref={video}
              controls
              playsInline
              preload="metadata"
              src={mediaUrl(info.asset_path)}
              poster={detail.scenes[0]?.key_path ? thumbUrl(detail.scenes[0].key_path) : undefined}
              onTimeUpdate={(e) => setPlayerMs(Math.round(e.currentTarget.currentTime * 1000))}
              className="max-h-[70vh] w-full rounded-[var(--radius-card)] bg-black"
            />
          ) : (
            <div className="flex aspect-[9/16] w-full flex-col items-center justify-center gap-2 rounded-[var(--radius-card)] bg-bg-subtle text-[12.5px] text-fg-faint">
              <Film size={24} />
              ещё не скачан
            </div>
          )}
          {info.duration_ms ? (
            <SceneRibbon scenes={detail.scenes} totalMs={info.duration_ms} currentMs={playerMs} onSeek={seek} />
          ) : null}
          <RefSource info={info} />
        </div>

        <div className="grid min-w-0 content-start gap-4">
          <RefActions detail={detail} beforePaid={notes.drain} />

          <Panel title="Общий комментарий" aside={notes.saving ? "сохраняю…" : undefined} annot="видео-референс: общий комментарий">
            <textarea
              value={notes.value(REF_NOTE, info.note)}
              disabled={info.status !== "ready"}
              maxLength={2000}
              rows={3}
              placeholder="что взять из ролика, что не брать — уйдёт композеру"
              aria-label="общий комментарий к референсу"
              onChange={(e) => notes.edit(REF_NOTE, e.target.value)}
              className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-1.5 text-[12.5px] text-fg outline-none placeholder:text-fg-faint focus:border-accent disabled:opacity-60"
            />
            <ErrorLine text={notes.error ? `не сохранено: ${refErrorText(notes.error)}` : ""} className="mt-2" />
          </Panel>

          {info.summary ? (
            <Panel
              title="Описание ролика"
              aside={`${usd(info.describe_cost)} · ${when(info.date_describe)}`}
              annot="видео-референс: сводка описания"
            >
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
                {SUMMARY.filter((s) => info.summary?.[s.key]).map((s) => (
                  <div key={s.key} className="contents">
                    <dt className="text-fg-faint">{s.label}</dt>
                    <dd className="text-fg">{info.summary?.[s.key]}</dd>
                  </div>
                ))}
              </dl>
            </Panel>
          ) : null}

          <Scenes detail={detail} playerMs={playerMs} seek={seek} notes={notes} />

          <Panel title="Проекты по этому референсу" annot="видео-референс: проекты">
            {detail.projects.length === 0 ? (
              <Empty>Проектов пока нет</Empty>
            ) : (
              <ul className="grid gap-1 text-[12.5px]">
                {detail.projects.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-baseline gap-x-2">
                    <Link to={`/project/${p.id}`} className="text-fg hover:text-accent-text">
                      {p.name}
                    </Link>
                    {p.dress_id ? (
                      <Link to={`/photos/${p.dress_id}`} className="text-fg-muted hover:text-accent-text">
                        платье {p.dress_id}
                      </Link>
                    ) : null}
                    <span className="text-fg-faint">{when(p.date_create)}</span>
                    <span className={p.scenario_applied ? "text-fg-muted" : "text-fg-faint"}>
                      {p.scenario_applied ? "сценарий применён" : "без сценария"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

/** Сцены: «добавить сцену», счётчик, карточки. Правки — `useSceneOps`, ошибка последней — строкой над списком. */
function Scenes({
  detail,
  playerMs,
  seek,
  notes,
}: {
  detail: VideoRefDetail;
  playerMs: number;
  seek: (ms: number) => void;
  notes: ReturnType<typeof useNoteAutosave>;
}) {
  const info = detail.ref;
  const ops = useSceneOps(info.id);
  const [opError, setOpError] = useState<unknown>(null);
  const ready = info.status === "ready";
  const busy = [ops.add, ops.patch, ops.split, ops.merge, ops.drop].some((m) => m.isPending);
  const done = { onSuccess: () => setOpError(null), onError: (e: Error) => setOpError(e) };
  const scenes = detail.scenes;

  return (
    <Panel title="Сцены" aside={scenes.length ? markedLabel(scenes) : undefined} annot="видео-референс: сцены">
      <div className="grid gap-2">
        {info.duration_ms ? (
          <AddSceneForm
            scenes={scenes}
            totalMs={info.duration_ms}
            minMs={detail.limits.min_scene_ms}
            disabled={!ready || busy}
            onAdd={(body) => ops.add.mutate(body, done)}
          />
        ) : null}
        {!ready && scenes.length > 0 ? <Hint>референс ещё разбирается — правки после разбора</Hint> : null}
        <ErrorLine text={opError ? refErrorText(opError) : ""} />
        {scenes.length === 0 ? <Empty>{ready ? "Сцен нет — добавьте вручную или переразберите" : "Сцен пока нет"}</Empty> : null}
        {scenes.map((s, i) => (
          <SceneCard
            key={s.id}
            scene={s}
            playerMs={playerMs}
            minSceneMs={detail.limits.min_scene_ms}
            cuts={info.split?.cuts}
            note={notes.value(sceneNoteKey(s.id), s.note)}
            editable={ready}
            busy={busy}
            hasNext={i < scenes.length - 1}
            onMark={(mark) => ops.patch.mutate({ sceneId: s.id, mark }, done)}
            onNote={(text) => notes.edit(sceneNoteKey(s.id), text)}
            onKey={(ms) => ops.patch.mutate({ sceneId: s.id, key_ms: ms }, done)}
            onSplit={(ms) => ops.split.mutate({ sceneId: s.id, at_ms: ms }, done)}
            onMerge={() => ops.merge.mutate(s.id, done)}
            onDrop={() => ops.drop.mutate(s.id, done)}
            onSeek={seek}
          />
        ))}
      </div>
    </Panel>
  );
}
